import { FC, forwardRef, useId, useImperativeHandle, useState } from 'react';
import { stringOrRichTextToString } from '@belong/contentful';
import { useAnalyticsEvents } from '@belong/providers/analyticsEvents'; // if you import from `@belong/providers` only, the InvoiceHistory.test.tsx will fail
import { ICON_SIZES } from '@belong/themes';
import { Copy } from '../../../styles/Typography/Copy';
import CollapsibleItem from '../../CollapsibleItem';
import { AccordionPlusMinus } from './AccordionPlusMinus';
import * as Accordion from './AccordionRow.styles';
import { ACCORDION_ROW_STATE, IAccordionRow } from './AccordionRow.types';

const ACCORDION_DROPDOWN_TEST_ID = 'accordion-dropdown'; // fallback constant if testIds module does not exist

export type ToggleIndexHandle = {
  toggleIndex: () => void;
};

export const AccordionRow: FC<IAccordionRow> = forwardRef<ToggleIndexHandle, IAccordionRow>(
  (
    {
      label,
      headingLevel,
      children,
      icon,
      variant,
      available = true,
      hasBackground = 'transparent',
      hasPadding,
      headingHasVerticalBorder = false,
      initialState = ACCORDION_ROW_STATE.closed,
      hasBottomBorder = false,
      unmountCollapsedContent = true,
      onClick,
      ...otherProps
    },
    ref
  ) => {
    const [isOpen, toggleOpen] = useState<boolean>(initialState === ACCORDION_ROW_STATE.open);
    const analyticsEvents = useAnalyticsEvents();

    const contentId = `${useId()}-content`;

    useImperativeHandle(ref, () => ({
      toggleIndex() {
        toggleOpen(!isOpen);
      }
    }));

    const toggleIndex = (): void => {
      toggleOpen(!isOpen);
    };

    const handleClick = (event): void => {
      event.preventDefault();

      toggleIndex();

      if (onClick !== undefined) {
        onClick(event);
      }

      analyticsEvents.send({
        category: 'page engagement',
        action: `accordion ${!isOpen ? 'expand' : 'collapse'}`,
        label: stringOrRichTextToString(label)
      });
    };

    const accordionHeader = (
      <Accordion.Button
        hasBackground={hasBackground}
        hasVerticalBorder={headingHasVerticalBorder}
        onClick={handleClick}
        aria-expanded={isOpen}
        aria-controls={contentId}
        data-testid={ACCORDION_DROPDOWN_TEST_ID}
        variant={variant}
        available={available}
      >
        <Accordion.StyledIconContainer>
          {icon && (
            <Accordion.StyledIcon name={icon.name} size={ICON_SIZES.small} className="accordion-row-inline-icon" />
          )}
          <Accordion.Label className="accordion-label">{label}</Accordion.Label>
        </Accordion.StyledIconContainer>
        <AccordionPlusMinus isOpen={isOpen} size={variant === 'slim' ? 'xsmall' : 'small'} />
      </Accordion.Button>
    );

    return (
      <Accordion.Wrapper
        isOpen={isOpen}
        variant={variant}
        hasBottomBorder={hasBottomBorder}
        className="accordion-wrapper"
        hasBackground={hasBackground}
        {...otherProps}
      >
        <Copy className="accordion-row--heading" as={headingLevel ?? 'span'} variant="medium" isBold>
          {accordionHeader}
        </Copy>

        <div id={contentId}>
          <CollapsibleItem isOpened={isOpen} shouldUnmount={unmountCollapsedContent} className="accordion-row--content">
            <Accordion.Body hasPadding={hasPadding} variant={variant}>
              {children}
            </Accordion.Body>
          </CollapsibleItem>
        </div>
      </Accordion.Wrapper>
    );
  }
);

AccordionRow.displayName = 'AccordionRow';
